import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Groups as GroupsIcon,
  PersonAdd as PersonAddIcon,
  Settings,
  Logout,
  AccountCircle,
  Email,
  AdminPanelSettings,
  FitnessCenter as CheckInIcon,
  ExpandLess,
  ExpandMore,
  CreditCard as PaymentIcon,
  Receipt as ReceiptIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Assessment as AssessmentIcon,
  Apartment as ApartmentIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import LegendsLogo from '../../assets/Legends-logo.jpg';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [membersOpen, setMembersOpen] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isConnected } = useSocket();

  // Auto-open Members submenu when on Members-related pages
  useEffect(() => {
    const membersPages = ['/members', '/members/deleted', '/daily-checkins', '/members/add'];
    const isOnMembersPage = membersPages.some(page => location.pathname.startsWith(page));
    if (isOnMembersPage) {
      setMembersOpen(true);
    }

    const paymentsPages = ['/payments', '/add-payment'];
    const isOnPaymentsPage = paymentsPages.some(page => location.pathname.startsWith(page));
    if (isOnPaymentsPage) {
      setPaymentsOpen(true);
    }
  }, [location.pathname]);

  // Function to get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;

    if (path === '/dashboard') return 'Dashboard';
    if (path === '/members') return 'Members';
    if (path === '/members/deleted') return 'Deleted Members';
    if (path === '/members/add') return 'Add Member';
    if (path.startsWith('/members/') && path.endsWith('/edit')) return 'Edit Member';
    if (path.startsWith('/members/') && !path.endsWith('/edit') && !path.endsWith('/add')) return 'Member Details';
    if (path === '/daily-checkins') return 'Daily Check-ins';
    if (path === '/checkins') return 'Check-ins';
    if (path === '/payments') return 'Payment History';
    if (path === '/add-payment') return 'Add Payment';
    if (path === '/send-email') return 'Send Email';
    if (path === '/settings') return 'Settings';
    if (path === '/admin/users') return 'Admin Users';

    // Default fallback
    return 'Dashboard';
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/login');
  };

  const handleMembersClick = () => {
    setMembersOpen(!membersOpen);
  };

  const handlePaymentsClick = () => {
    setPaymentsOpen(!paymentsOpen);
  };

  const menuItems = [
    { text: 'Dashboard', icon: <HomeIcon sx={{ color: '#1976d2' }} />, path: '/dashboard' },
    {
      text: 'Members',
      icon: <GroupsIcon sx={{ color: '#2e7d32' }} />,
      path: '/members',
      hasSubmenu: true,
      submenu: [
        { text: 'View Members', icon: <ViewIcon sx={{ color: '#2e7d32' }} />, path: '/members' },
        { text: 'Deleted Members', icon: <ViewIcon sx={{ color: '#9e9e9e' }} />, path: '/members/deleted' },
        { text: 'Daily Check-ins', icon: <CheckInIcon sx={{ color: '#ed6c02' }} />, path: '/daily-checkins' },
        { text: 'Add Member', icon: <PersonAddIcon sx={{ color: '#1976d2' }} />, path: '/members/add' },
      ]
    },
    { text: 'Estate Address Overview', icon: <ApartmentIcon sx={{ color: '#0288d1' }} />, path: '/estate-addresses/overview' },
    {
      text: 'Payments',
      icon: <PaymentIcon sx={{ color: '#9c27b0' }} />,
      path: '/payments',
      hasSubmenu: true,
      submenu: [
        { text: 'Payment History', icon: <ReceiptIcon sx={{ color: '#9c27b0' }} />, path: '/payments' },
        { text: 'Add Payment', icon: <AddIcon sx={{ color: '#1976d2' }} />, path: '/add-payment' },
      ]
    },
    { text: 'Send Email', icon: <Email sx={{ color: '#d32f2f' }} />, path: '/send-email' },
    { text: 'Reports', icon: <AssessmentIcon sx={{ color: '#1976d2' }} />, path: '/reports' },
    ...(user?.role === 'admin' ? [{ text: 'Admin Users', icon: <AdminPanelSettings sx={{ color: '#ff5722' }} />, path: '/admin/users' }] : []),
    { text: 'Settings', icon: <Settings sx={{ color: '#616161' }} />, path: '/settings' },
  ];

  const drawer = (
    <div>
      <Toolbar sx={{
        height: 150, // Same height as AppBar
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="img"
            src={LegendsLogo}
            sx={{ height: 128, width: 'auto', borderRadius: '50%' }}
          />
          {/* <Typography variant="h6" noWrap component="div" color="primary">
            Legends Gym
          </Typography> */}
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <div key={item.text}>
            <ListItem disablePadding>
              <ListItemButton
                selected={!item.hasSubmenu && location.pathname === item.path}
                onClick={() => {
                  if (item.hasSubmenu) {
                    if (item.text === 'Members') {
                      handleMembersClick();
                    } else if (item.text === 'Payments') {
                      handlePaymentsClick();
                    }
                  } else {
                    navigate(item.path);
                  }
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
                {item.hasSubmenu ? (
                  (item.text === 'Members' ? membersOpen : paymentsOpen) ? <ExpandLess /> : <ExpandMore />
                ) : null}
              </ListItemButton>
            </ListItem>
            {item.hasSubmenu && (
              <Collapse in={item.text === 'Members' ? membersOpen : paymentsOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.submenu?.map((subItem) => (
                    <ListItem key={subItem.text} disablePadding>
                      <ListItemButton
                        sx={{ pl: 4 }}
                        selected={location.pathname === subItem.path}
                        onClick={() => navigate(subItem.path)}
                      >
                        <ListItemIcon>{subItem.icon}</ListItemIcon>
                        <ListItemText primary={subItem.text} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </div>
        ))}
      </List>
      <Box sx={{ position: 'absolute', bottom: 16, left: 16, right: 16, borderTop: '1px solid #e2e8f0', pt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: isConnected ? '#38a169' : '#e53e3e',
              mr: 1,
            }}
          />
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#4a5568' }}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </Typography>
        </Box>
      </Box>
    </div>

  );

  const isMenuOpen = Boolean(anchorEl);

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id="primary-search-account-menu"
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleMenuClose}>
        <ListItemIcon>
          <AccountCircle fontSize="small" />
        </ListItemIcon>
        Profile
      </MenuItem>
      <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}>
        <ListItemIcon>
          <Settings fontSize="small" />
        </ListItemIcon>
        Settings
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleLogout}>
        <ListItemIcon>
          <Logout fontSize="small" />
        </ListItemIcon>
        Logout
      </MenuItem>
    </Menu>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          height: 152, // Same height as the logo container
        }}
      >
        <Toolbar sx={{ height: '100%', alignItems: 'flex-start', pt: 2 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, mt: 1 }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '152px' }}>


            <Typography variant="h4" noWrap component="div" sx={{
              color: '#1a202c',
              fontWeight: 700,
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center'
            }}>
              {getPageTitle()}
            </Typography>
          </Box>


          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Typography variant="h6" noWrap component="div" sx={{
              color: '#1a202c',
              fontWeight: 600,
              mb: 1
            }}>
              Gym Management System
            </Typography>

            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.firstName?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>

        {/* Bottom border line */}
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          backgroundColor: '#e5e9f0'
        }} />
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          background: 'linear-gradient(135deg, #f9fafb 0%, #fafbfc 100%)',
          minHeight: '100vh',
          pt: '170px', // Account for the larger AppBar height (152px + some padding)
        }}
      >
        {children}
      </Box>

      {renderMenu}
    </Box>
  );
};

export default Layout;